import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { 
  Box,
  Button,
  Container,
  TextField,
  Paper,
  Typography,
  Autocomplete,
  Chip,
  Stack,
  IconButton,
  useTheme,
  Grid,
  FormControl,
  FormHelperText,
  CircularProgress, 
  // Radio,
  // RadioGroup,
  // FormControlLabel,
  // FormControl,
  // FormLabel,
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import DeleteIcon from '@mui/icons-material/Delete';
import {  MainContentArea, MainContentAreaChildGrid } from './styled';
import { useNavigate } from 'react-router-dom';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { useDropzone } from 'react-dropzone';
import useCurrentUserInfo from '../../hooks/useCurrentUserInfo';
import { useDispatch, useSelector } from 'react-redux';
import { chefUploadRecipe } from '../../redux/actions/recipeActions';
import TinyMceEditor from '../../components/TinyMceEditor';
import RecipeFileUpload from './RecipeFileUpload';

function AddRecipe() {

  const theme = useTheme();
  const navigate = useNavigate();
  const userInfo = useCurrentUserInfo();
  const dispatch = useDispatch();

  const chefUploadRecipeData = useSelector((state) => state?.chefAdminRecipeData);
  const {
    isLoading,
    successUpload,
    lastUploadedId
  } = chefUploadRecipeData;

  const [selectedFile, setSelectedFile] = useState(null);
  const [recipeContent, setRecipeContent] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const [recipeData, setRecipeData] = useState({
    title: '',
    author: userInfo?.name ?? '',
    picture: '',
    picturePreview: '',
    tags: [],
    ingredients: [],
    recipeContent: recipeContent,
    prepTime: '',
    cookTime: '',
    totalTime: '',
    servings: '',
    difficulty: '',
    associatedVideoId: '',
    recipeOwnerId: userInfo?.user_id
  });

  const availableTags = ['Vegetarian', 'Vegan', 'Gluten-Free', 'Quick', 'Dessert', 'Main Course'];

  const allRequiredFields = () => {
    const {
      title,
      author,
      picture,
      tags,
      ingredients,
      recipeContent,
      recipeOwnerId
    } = recipeData;

    if(
      title &&
      author &&
      picture &&
      tags &&
      ingredients &&
      recipeContent &&
      recipeOwnerId
    ){
      return false
    };

    return true;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    dispatch(
      chefUploadRecipe({
        recipeData: recipeData,
        userId: userInfo?.user_id
      })
    , dispatch);
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      // Create a preview URL for the image
      const previewUrl = URL.createObjectURL(file);
      
      // Convert the file to base64 for Cloudinary upload
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        setRecipeData(prev => ({
          ...prev,
          picture: reader.result, // This will be the base64 string
          picturePreview: previewUrl
        }));
      };
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png']
    },
    multiple: false
  });

  useEffect(() => {
    if(successUpload){
      navigate(`/edit-recipe-details/${lastUploadedId}`)
    }
  }, [successUpload]);

  // Replace the image upload button with this dropzone component
  const imageUploadSection = (
    <Box
      {...getRootProps()}
      sx={{
        border: '2px dashed',
        borderColor: isDragActive ? 'primary.main' : 'grey.300',
        borderRadius: 1,
        p: 2,
        cursor: 'pointer',
        '&:hover': {
          borderColor: 'primary.main',
        },
      }}
    >
      <input {...getInputProps()} />
      <Stack spacing={1} alignItems="center">
        <ImageIcon color="action" />
        <Typography variant="body2" color="textSecondary" align="center">
          {isDragActive
            ? "Drop the image here"
            : "Drag and drop an image here, or click to select"}
        </Typography>
      </Stack>
    </Box>
  );

  // Preview section
  const imagePreview = (
    recipeData.picturePreview && (
      <Paper sx={{ mt: 2, position: 'relative', display: 'flex', justifyContent: 'center', padding: '1rem' }}>
        <img
          src={recipeData.picturePreview}
          alt="Recipe preview"
          style={{
            maxWidth: '100%',
            maxHeight: '200px',
            objectFit: 'contain',
          }}
        />
        <IconButton
          size="small"
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
          }}
          onClick={(e) => {
            e.stopPropagation();
            URL.revokeObjectURL(recipeData.picturePreview);
            setRecipeData(prev => ({
              ...prev,
              picture: null,
              picturePreview: null
            }));
          }}
        >
          <DeleteIcon sx={{fontSize: '2rem'}} />
        </IconButton>
      </Paper>
    )
  );

  return (
    <MainContentArea elevation={0}>
      <MainContentAreaChildGrid container >
        <Grid 
          sx={{ 
            backgroundColor: `${theme.palette.primary.main}`,
            height: '3rem',
            width: '100%',
            padding: '1rem 1rem',
            borderBottomLeftRadius: '2rem', 
            borderBottomRightRadius: '2rem',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
          }}
        >
          <Button
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-around',
              width: '6rem'
            }}
            onClick={() => navigate(-1)}
            color='secondary'
          >
            <ArrowBackOutlinedIcon />
            <Typography variant='h7' color={'secondary'}>
                Back
              </Typography>
          </Button>
        </Grid>
          <Container maxWidth="md" sx={{ py: 4, height: '100%' }}>
            { isLoading ? (
              <Grid 
                display={'flex'} 
                alignItems={'center'} 
                justifyContent={'center'} 
                sx={{ width: '100%', height: '100%', flexGrow: 1}}
              >
                <CircularProgress size={150}/>

              </Grid>
            ) : (
              <Stack spacing={3}>
                <RecipeFileUpload
                  setRecipeContent={setRecipeContent} 
                  setRecipeData={setRecipeData} 
                  selectedFile={selectedFile}
                  setSelectedFile={setSelectedFile}
                  isProcessing={isProcessing}
                  setIsProcessing={setIsProcessing}
                />

                {/* Main Recipe Details Section */}
                <Paper elevation={3} sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    <TextField
                      label="Recipe Title"
                      variant="outlined"
                      value={recipeData?.title}
                      disabled={isProcessing}
                      fullWidth
                      required
                      onChange={(e) => setRecipeData({...recipeData, title: e.target.value})}
                    />

                    <TextField
                      label="Recipe Author"
                      variant="outlined"
                      fullWidth
                      required
                      disabled={isProcessing}
                      value={recipeData?.author}
                      onChange={(e) => setRecipeData({...recipeData, author: e.target.value})}
                    />

                    <Autocomplete
                      multiple
                      options={availableTags}
                      disabled={isProcessing}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option}
                            {...getTagProps({ index })}
                            key={option}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Tags"
                          placeholder="Add tags"
                        />
                      )}
                      value={recipeData?.tags}
                      onChange={(event, newValue) => setRecipeData({...recipeData, tags: newValue})}
                    />
                    <FormControl fullWidth>
                      <Autocomplete
                        disabled={isProcessing}
                        multiple
                        freeSolo
                        options={[]}
                        value={recipeData?.ingredients || []}
                        onChange={(event, newValue) => setRecipeData({...recipeData, ingredients: newValue})}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' && event.target.value.trim()) {
                            event.preventDefault();
                            
                            const newIngredient = event.target.value.trim();
                            
                            if (!recipeData?.ingredients?.includes(newIngredient)) {
                              setRecipeData({
                                ...recipeData,
                                ingredients: [...(recipeData?.ingredients || []), newIngredient]
                              });
                            }
                            
                            event.target.value = '';
                          }
                        }}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              label={option}
                              {...getTagProps({ index })}
                              key={option}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Ingredients"
                            placeholder="Add Ingredients"
                          />
                        )}
                      />
                      <FormHelperText>
                        Start typing then press 'Enter' to add an ingredient
                      </FormHelperText>
                    </FormControl>
                    <Grid sx={{ height: '100%'}}>
                    <TinyMceEditor 
                      content={recipeContent}
                      setContent={setRecipeContent}
                      disabled={isProcessing}
                    />
                    </Grid>

                    {!recipeData?.picturePreview && imageUploadSection}
                    {imagePreview}
                  </Stack>
                </Paper>

                {/* Video Section */}
                {/* <Paper elevation={3} sx={{ p: 2 }}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Video Option</FormLabel>
                    <RadioGroup
                      value={videoOption}
                      onChange={(e) => setVideoOption(e.target.value)}
                    >
                      <FormControlLabel
                        value="link"
                        control={<Radio />}
                        label="Video Link"
                      />
                      <FormControlLabel
                        value="upload"
                        control={<Radio />}
                        label="Search Uploaded Videos"
                      />
                    </RadioGroup>
                  </FormControl>

                  {videoOption === 'link' ? (
                    <TextField
                      label="Video URL"
                      variant="outlined"
                      fullWidth
                      sx={{ mt: 2 }}
                    />
                  ) : (
                    <Button
                      variant="outlined"
                      fullWidth
                      sx={{ mt: 2 }}
                    >
                      Browse Uploaded Videos
                    </Button>
                  )}
                </Paper> */}

                {/* Submit Button */}
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  disabled={allRequiredFields()}
                  onClick={handleSubmit}
                  sx={{ marginBottom: '1rem' }}
                >
                  Complete Recipe Upload
                </Button>
              </Stack>
            )
          }
        </Container>
      </MainContentAreaChildGrid>
    </MainContentArea>
  )
}

export default AddRecipe;